import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AgentRuntime, ReceptionistEdit, SaveReceptionistResponse } from '../../../api/agentRuntimeSchemas';
import { ApiError } from '../../../api/client';
import {
  getAgentRuntime,
  getReceptionist,
  pauseReceptionist,
  resumeReceptionist,
  saveReceptionist,
} from '../../../api/endpoints/agentRuntime';

export const RUNTIME_KEY = ['agents-runtime'] as const;
export const RECEPTIONIST_KEY = ['agents-receptionist'] as const;
/** While something is being linked, the status is read often enough to feel live. */
export const LIVE_POLL_MS = 3000;
const IDLE_POLL_MS = 30_000;

/** The runtime's live status and this month's meter; polled faster while linking. */
export function useAgentRuntime(live = false) {
  return useQuery({
    queryKey: RUNTIME_KEY,
    queryFn: getAgentRuntime,
    refetchInterval: live ? LIVE_POLL_MS : IDLE_POLL_MS,
    retry: false,
  });
}

export function useReceptionistSettings() {
  return useQuery({ queryKey: RECEPTIONIST_KEY, queryFn: getReceptionist, retry: false });
}

export function useSaveReceptionist() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (edit: ReceptionistEdit) => saveReceptionist(edit),
    // An unsynced save already says "Guardado. Luna lo toma en cuanto se conecte." in the form.
    meta: { exito: (_edit, saved) => ((saved as SaveReceptionistResponse).synced ? 'Cambios guardados' : null) },
    onSuccess: (result) => client.setQueryData(RECEPTIONIST_KEY, result.data),
  });
}

export function usePauseToggle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (pause: boolean) => (pause ? pauseReceptionist() : resumeReceptionist()),
    onSuccess: (settings) => {
      client.setQueryData(RECEPTIONIST_KEY, settings);
      void client.invalidateQueries({ queryKey: RUNTIME_KEY });
    },
  });
}

/** Before Martín connects the runtime, every call answers 503 agents_unavailable. */
export function isRuntimeOff(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'agents_unavailable' || error.status === 503);
}

export type SetupStep = 'whatsapp' | 'settings' | 'try';

/**
 * Which steps are done, from the runtime's own status and whether settings were
 * ever saved. ChatGPT is not a step: Luna runs on Dale's account, connected
 * from the admin, and answers with the fallback until then.
 */
export function setupProgress(runtime: AgentRuntime | undefined, settingsSaved: boolean): Record<SetupStep, boolean> {
  const whatsapp = runtime?.status.whatsapp === 'linked';
  return { whatsapp, settings: settingsSaved, try: whatsapp && settingsSaved && runtime?.status.lastTurnAt != null };
}

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}
